import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { Box, Collapse, IconButton, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { RuleTypeEnum } from '../../../../graphql/generated';
import { WarningBanner } from '../../atoms';
import type { FormValues } from '../../organisms/AddRuleDrawer/useAddRuleDrawer';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { SwitchField } from '../SwitchField/SwitchField';

interface Props {
  control: Control<FormValues>;
}

export const ActionsSection: React.FC<Props> = ({ control }) => {
  const theme = useTheme<LensTheme>();
  const sp = theme.tokens.semantic.spacing.desktop.gap;
  const color = theme.tokens.semantic.color;
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: sp[3].value,
        paddingTop: sp[4].value,
        borderTop: `1px solid ${color.ui.divider.default.value}`,
      }}
    >
      <SectionHeader
        title="Actions"
        subtitle="Define what happens to case data when this retention policy is triggered"
      />

      <Controller
        name="actionMode"
        control={control}
        render={({ field }) => (
          <>
            {field.value === RuleTypeEnum.Deletion && (
              <WarningBanner
                title="Warning: This action cannot be undone"
                message={`Improper deletion may affect reports and breach compliance.\nChanges won't be permanent until the rule is running.`}
              />
            )}
            <SwitchField
              checked={field.value === RuleTypeEnum.Anonymization}
              disabled={field.value === RuleTypeEnum.Deletion}
              label="Sanitize All"
              description="Remove PII from all fields"
              onChange={() =>
                field.onChange(
                  field.value === RuleTypeEnum.Anonymization
                    ? null
                    : RuleTypeEnum.Anonymization,
                )
              }
            />
          </>
        )}
      />

      <Controller
        name="actionMode"
        control={control}
        render={({ field }) => (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
              }}
              onClick={() => setIsDeleteOpen((prev) => !prev)}
            >
              <IconButton size="small" sx={{ padding: 0 }}>
                {isDeleteOpen ? <ExpandMore /> : <ChevronRight />}
              </IconButton>
              <Typography sx={{ fontSize: '16px', fontWeight: 400 }}>
                Delete complete case
              </Typography>
            </Box>

            <Collapse in={isDeleteOpen}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 300,
                  mt: theme.tokens.semantic.spacing.desktop.gap['0_5'].value,
                }}
              >
                Permanently delete the entire case and all associated data.
              </Typography>
              <Box
                sx={{
                  mt: sp[2].value,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: sp[2].value,
                }}
              >
                <SwitchField
                  checked={field.value === RuleTypeEnum.Deletion}
                  disabled={false}
                  label="Delete case"
                  description=""
                  onChange={() =>
                    field.onChange(
                      field.value === RuleTypeEnum.Deletion
                        ? null
                        : RuleTypeEnum.Deletion,
                    )
                  }
                />
              </Box>
            </Collapse>
          </Box>
        )}
      />
    </Box>
  );
};
